import { useEffect, useState } from 'react'
import Spinner from 'app/components/Spinner'
import { Tooltip } from 'app/components/Tooltip'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

type ListColumnProps = {
  ids?: string[]
  entityName: string
  allText?: boolean
}

export default function ListColumn({ ids, entityName, allText }: ListColumnProps): React.ReactElement {
  let [firstItemName, setFirstItemName] = useState<{ id: string; name: string }>()
  let [names, setNames] = useState<string[]>()
  let [loading, setLoading] = useState<boolean>(false)

  // Stringify ids to prevent re-fetching when array reference changes but values are the same
  const idsKey = ids?.join(`,`) ?? ``

  useEffect(() => {
    if (!ids || ids?.length === 0) return
    bg(async () => {
      setLoading(true)
      try {
        // Scope the term to the `id` field. A bare quoted-OR search (`"a" OR "b" ...`) has no
        // field, so OpenSearch fans each term across every mapped field on the index; on the
        // ~294-field location index that exceeds maxClauseCount (1024) and 500s.
        const search = `id:(${ids.map((id) => `"${id}"`).join(` OR `)})`
        const result = await gqlFetchList<{ id: string; name: string }>(
          entityName,
          { search, pagination: { limit: ids.length } },
          'id name',
        )
        if (result.total > 0) {
          setFirstItemName({ id: result.hits[0].id, name: result.hits[0].name })
          setNames(result.hits.slice(1).map((h) => h.name))
        }
      } catch (e) {
        logError(`Error fetching names for ids ${ids}`, e)
        setFirstItemName({ id: `-1`, name: `Unknown` })
      }

      setLoading(false)
    })
  }, [idsKey])

  const renderAll = () => {
    // If ids is undefined or a user has location ids that are not valid, eg. archived, then show "All"
    if (allText) return <span className="table-list-column inline-flex">All</span>
    return <></>
  }

  if (!ids) renderAll()

  if (loading)
    return (
      <span key={firstItemName?.id} className="table-list-column inline-flex">
        <Spinner />
      </span>
    )

  return ids && firstItemName?.name ? (
    <div className="flex gap-x-[6px]">
      <span key={firstItemName?.id} className="table-list-column">
        {firstItemName?.name}
      </span>
      {names && names?.length > 0 && (
        <Tooltip
          message={
            names && (
              <>
                {names!.map((m) => (
                  <div key={m}>{m}</div>
                ))}
              </>
            )
          }
        >
          <span className="table-list-column inline-flex">{`+ ${names.length}`}</span>
        </Tooltip>
      )}
    </div>
  ) : (
    renderAll()
  )
}
